import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Layout from '../../components/Layout';
import { salesItemService, SalesItemDTO } from '../../services/salesItemService';
import { parseApiError } from '../../utils/errorUtils';
import '../../pages/Products/ProductCategoryDeletePage.css';
import { ArrowLeft, AlertTriangle, Trash2 } from 'lucide-react';

const SalesItemDeletePage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [item, setItem] = useState<SalesItemDTO | null>(null);
  const [loading, setLoading] = useState(true);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadItem();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const loadItem = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await salesItemService.getSalesItemById(parseInt(id || '0'));
      if (response?.data?.data) {
        setItem(response.data.data);
      }
      setLoading(false);
    } catch (err) {
      setError(t('salesItem.failedToLoadItem'));
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await salesItemService.deleteSalesItem(parseInt(id || '0'));
      navigate('/sales-items', { state: { message: t('salesItem.deletedSuccess') } });
    } catch (err: unknown) {
      setError(parseApiError(err, t('salesItem.failedToDeleteItem')).message);
      setDeleting(false);
    }
  };

  if (loading) {
    return (
      <Layout>
        <div className="delete-loading-container">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">{t('common.loading')}</span>
          </div>
        </div>
      </Layout>
    );
  }

  if (!item) {
    return (
      <Layout>
        <div className="delete-container">
          <div className="alert alert-danger">{error || t('salesItem.itemNotFound')}</div>
          <button className="btn btn-secondary" onClick={() => navigate('/sales-items')}>
            {t('salesItem.backToList')}
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="delete-container">
        <nav className="delete-breadcrumb">
          <button
            className="delete-breadcrumb-btn"
            onClick={() => navigate(`/sales-items/${id}`)}
          >
            <ArrowLeft size={16} strokeWidth={2} />
            {t('salesItem.backToItem')}
          </button>
        </nav>

        <div className="delete-header">
          <div className="delete-warning-icon">
            <AlertTriangle size={32} strokeWidth={2} />
          </div>
          <div>
            <h1 className="delete-title">{t('salesItem.deleteTitle')}</h1>
            <p className="delete-subtitle">{t('salesItem.deleteConfirmationMessage')}</p>
          </div>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
            <button
              type="button"
              className="btn-close"
              onClick={() => setError(null)}
              aria-label={t('common.close')}
            ></button>
          </div>
        )}

        <div className="delete-content">
          <div className="delete-main-section">
            <div className="delete-card">
              <div className="delete-card-header">
                <h2 className="delete-card-title">{t('salesItem.itemDetails')}</h2>
              </div>
              <div className="delete-card-body">
                <div className="delete-item-info">
                  <div className="delete-field">
                    <span className="delete-field-label">{t('salesItem.productSKU')}</span>
                    <span className="delete-code">{item.productSKU}</span>
                  </div>
                  <div className="delete-field">
                    <span className="delete-field-label">{t('salesItem.productName')}</span>
                    <span className="delete-field-value">{item.productName}</span>
                  </div>
                  <div className="delete-field">
                    <span className="delete-field-label">{t('salesItem.sellingPrice')}</span>
                    <span className="delete-field-value">
                      <strong>{Number(item.sellingPrice).toFixed(2)} {item.currency}</strong>
                    </span>
                  </div>
                  <div className="delete-field">
                    <span className="delete-field-label">{t('salesItem.salesChannel')}</span>
                    <span className="delete-field-value">{item.salesChannel}</span>
                  </div>
                </div>

                <div className="delete-actions">
                  <button
                    className="btn-delete"
                    onClick={handleDelete}
                    disabled={deleting}
                  >
                    {deleting ? (
                      <>
                        <span className="btn-spinner"></span>
                        {t('common.deleting')}
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} strokeWidth={2} />
                        {t('salesItem.deleteItem')}
                      </>
                    )}
                  </button>
                  <button
                    className="btn-cancel"
                    onClick={() => navigate(`/sales-items/${id}`)}
                    disabled={deleting}
                  >
                    {t('common.cancel')}
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="delete-sidebar">
            <div className="delete-card delete-card-warning">
              <div className="delete-card-header">
                <h3 className="delete-card-title">{t('salesItem.safetyInformation')}</h3>
              </div>
              <div className="delete-card-body">
                <div className="delete-warning-item">
                  <strong>{t('salesItem.permanentAction')}</strong>
                  <p>{t('salesItem.deleteWarning')}</p>
                </div>
                <div className="delete-warning-item">
                  <strong>{t('salesItem.relatedData')}</strong>
                  <p>{t('salesItem.deleteRelatedDataWarning')}</p>
                </div>
                <div className="delete-warning-item">
                  <strong>{t('salesItem.confirmationRequired')}</strong>
                  <p>{t('salesItem.deleteConfirmationRequired')}</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default SalesItemDeletePage;
